import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { checkDLocalPaymentConfirmation } from 'features/payment/services';
import { manageDLocalResponseCode } from 'features/payment/helpers';
import { dLocalPollIntervalMs, maxDLocalAttempts } from 'features/payment/constants';
import { DLocalPaymentStatus } from 'features/payment/types';
import { extractErrorMessage } from 'features/quotation/helpers';

interface DLocalPaymentStatusPollerProps {
	orderId: string;
	active: boolean;
	compact?: boolean;
	onPaid: () => void;
	onFailed: (message: string) => void;
	onPollingExhausted: (message: string, lastStatus: string) => void;
}

export const DLocalPaymentStatusPoller = ({
	orderId,
	active,
	compact = false,
	onPaid,
	onFailed,
	onPollingExhausted,
}: DLocalPaymentStatusPollerProps): JSX.Element | null => {
	const [attempt, setAttempt] = useState(0);
	const [lastStatus, setLastStatus] = useState<string | null>(null);
	const [statusMessage, setStatusMessage] = useState<string | null>(null);
	const [exhausted, setExhausted] = useState(false);
	const [checkingNow, setCheckingNow] = useState(false);
	const attemptRef = useRef(0);

	useEffect(() => {
		attemptRef.current = attempt;
	}, [attempt]);

	const runPoll = useCallback(async (): Promise<void> => {
		try {
			const paymentStatus = (await checkDLocalPaymentConfirmation(orderId)).data.status;
			const statusLabel = String(paymentStatus);
			setLastStatus(statusLabel);
			const [alertType, alertMessage] = manageDLocalResponseCode(paymentStatus);

			if (alertType === 'success') {
				onPaid();
				return;
			}

			if (alertType === 'error' || paymentStatus === DLocalPaymentStatus.CANCELLED) {
				onFailed(alertMessage || 'No se pudo confirmar el pago en DLocal.');
				return;
			}

			const nextAttempt = attemptRef.current + 1;
			if (nextAttempt >= maxDLocalAttempts) {
				setExhausted(true);
				onPollingExhausted(alertMessage || 'El pago sigue en proceso de confirmación.', statusLabel);
				return;
			}

			setStatusMessage(alertMessage || 'Pago en proceso…');
			setAttempt(nextAttempt);
		} catch (error: unknown) {
			onFailed(extractErrorMessage(error, 'Error al consultar el estado del pago'));
		} finally {
			setCheckingNow(false);
		}
	}, [onFailed, onPaid, onPollingExhausted, orderId]);

	useEffect(() => {
		if (!active || exhausted) return undefined;
		const delay = attempt === 0 && lastStatus === null ? 0 : dLocalPollIntervalMs;
		const timer = window.setTimeout(() => {
			void runPoll();
		}, delay);
		return () => window.clearTimeout(timer);
	}, [active, attempt, exhausted, lastStatus, runPoll]);

	const handleVerifyNow = (): void => {
		if (checkingNow || exhausted) return;
		setCheckingNow(true);
		void runPoll();
	};

	if (!active && !exhausted) return null;

	const progressLabel =
		attempt > 0
			? `Consultando pago… (${attempt}/${maxDLocalAttempts})${lastStatus ? ` — último estado: ${lastStatus}` : ''}`
			: 'Iniciando consulta del pago…';

	return (
		<Box sx={{ py: compact ? 1 : 2 }}>
			{!exhausted && (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
						<Typography variant="body2" color="text.secondary">
							{progressLabel}
						</Typography>
						<Button
							size="small"
							variant="outlined"
							startIcon={<RefreshIcon />}
							onClick={handleVerifyNow}
							disabled={checkingNow}
						>
							Verificar ahora
						</Button>
					</Box>
					<LinearProgress sx={{ borderRadius: 1, mb: compact ? 1 : 2 }} />
					{statusMessage && (
						<Alert severity="info" sx={{ mt: 1 }}>
							{statusMessage}
						</Alert>
					)}
					{!compact && (
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
							Completá el pago en la pestaña de DLocal. Esta pantalla se actualiza automáticamente cada 10 segundos.
						</Typography>
					)}
				</>
			)}
		</Box>
	);
};
